import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ExportDeadlineDto } from '@/services/ws-invoice-sync/src/main/resources/rest-api';
import { getExportDeadlines } from '@/services/ws-invoice-sync/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { useSubscribe } from '@/js/libs/observable';
import { Button, Container, Icons, PageHeader, Table } from '@/js/libs/ui/src/V2';
import { useRequest } from '@/js/libs/use-request';

import TableHeaderV2 from 'src/components/TableHeaderV2';
import { BookingPeriodFormPopup } from './components/BookingPeriodFormPopup';
import type { BookingPeriodFormPopupPayload } from './components/BookingPeriodFormPopup/types';
import { useColumns } from './hooks';

export const BookingPeriods: React.FC = React.memo(function BookingPeriods() {
  const [t] = useTranslation(['backend', 'common', 'finances']);

  const columns = useColumns(t);

  const getExportDeadlinesRequest = useRequest({
    apiMethod: getExportDeadlines,
  });

  const { currentItems, total } = useSubscribe(getExportDeadlinesRequest.data$, (response) => ({
    currentItems: response?.data ?? [],
    total: response?.data?.length ?? 0,
  }));
  const pendingCurrentItems = useSubscribe(getExportDeadlinesRequest.pending$);

  const createExportDeadlineFormPopupState = useModalState<BookingPeriodFormPopupPayload>();
  const handleOpenExportDeadlineFormPopup = useCallbackRef((item?: ExportDeadlineDto) => {
    createExportDeadlineFormPopupState.open({
      item,
      callback: (data) =>
        getExportDeadlinesRequest.setData((state) => ({
          ...state,
          data: item ? state.data.map((stateItem) => (stateItem.id === data.id ? data : stateItem)) : [...state.data, data],
        })),
    });
  });

  return (
    <>
      {createExportDeadlineFormPopupState.isMounted && <BookingPeriodFormPopup state={createExportDeadlineFormPopupState} />}
      <PageHeader
        title={t('finances:BOOKING_PERIODS_TITLE')}
        actions={
          <Button primary onClick={() => handleOpenExportDeadlineFormPopup()} dataTestId='create-booking-period-button'>
            <Icons.Add alt />
            {t('finances:CREATE_BOOKING_PERIOD')}
          </Button>
        }
      />
      <Container fluid>
        <Table.Wrapper>
          <Table.Info total={total} isLoading={pendingCurrentItems} />
          <Table>
            <TableHeaderV2 columns={columns} />
            {!pendingCurrentItems &&
              currentItems.length > 0 &&
              currentItems.map((item) => (
                <Table.Row key={item.id.toString()} dataTestId='booking-period-row'>
                  {columns.map((column) => {
                    const { component: Component, name } = column;
                    return (
                      <Table.Cell {...column} key={name} dataTestId={`${name}-cell`}>
                        {Component && <Component item={item} column={column} onEdit={() => handleOpenExportDeadlineFormPopup(item)} />}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              ))}
            {pendingCurrentItems && <Table.Loader columnsLength={columns.length} />}
          </Table>
          {!currentItems.length && !pendingCurrentItems && <Table.NoData message={t('common:NO_TABLE_DATA_MESSAGE')} />}
          <Table.Info total={total} isLoading={pendingCurrentItems} />
        </Table.Wrapper>
      </Container>
    </>
  );
});
